import { DataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Multiselect,
  type MultiselectOption,
} from '@/components/ui/multiselect';
import {
  useAdminApiClientsService,
  useAdminCreateApiClientService,
  useAdminDeleteApiClientService,
  useAdminUpdateApiClientService,
  useBackOfficeWorkspacesService,
} from '@/services/backoffice';
import type { ApiClientItem } from '@ditto/shared';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Copy,
  Key,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { toast } from 'sonner';

const WorkspaceTagSelector: FC<{
  selectedWorkspaceIds: string[];
  setSelectedWorkspaceIds: (ids: string[]) => void;
}> = ({ selectedWorkspaceIds, setSelectedWorkspaceIds }) => {
  const { workspaces } = useBackOfficeWorkspacesService();

  const workspaceOptions: MultiselectOption[] = useMemo(
    () =>
      workspaces.map(w => ({
        label: `${w.name} (${w.uname})`,
        value: w.id,
      })),
    [workspaces]
  );

  const selectedWorkspaces = useMemo(
    () => workspaces.filter(w => selectedWorkspaceIds.includes(w.id)),
    [workspaces, selectedWorkspaceIds]
  );

  const removeWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds(
      selectedWorkspaceIds.filter(id => id !== workspaceId)
    );
  };

  const customTrigger = (
    <div className="min-h-[40px] w-full border rounded-md px-3 py-2 bg-background">
      {selectedWorkspaces.length === 0 ? (
        <span className="text-muted-foreground">Select workspaces...</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selectedWorkspaces.map(workspace => (
            <div
              key={workspace.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              <span className="font-mono">{workspace.uname}</span>
              <button
                type="button"
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeWorkspace(workspace.id);
                }}
                className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      <Label>Workspaces</Label>
      <Multiselect
        options={workspaceOptions}
        selected={selectedWorkspaceIds}
        onChange={setSelectedWorkspaceIds}
        placeholder="Select workspaces..."
        showSearch
        showClearAll
        contentClassName="max-h-[300px] overflow-y-auto w-full"
        trigger={customTrigger}
      />
      <p className="text-xs text-muted-foreground">
        {selectedWorkspaceIds.length === 0
          ? 'No workspaces selected'
          : selectedWorkspaceIds.length === 1
            ? '1 workspace selected'
            : `${selectedWorkspaceIds.length} workspaces selected`}
      </p>
    </div>
  );
};

export const ApiClientsPage: FC = () => {
  const { apiClients, isLoading } = useAdminApiClientsService();
  const { deleteApiClient } = useAdminDeleteApiClientService();
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<ApiClientItem | null>(null);

  const columns = useMemo<ColumnDef<ApiClientItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'keyPrefix',
        header: 'Key',
        cell: ({ row }) => (
          <code className="text-xs font-mono bg-neutral-100 px-2 py-1 rounded">
            {row.original.keyPrefix}...
          </code>
        ),
      },
      {
        id: 'workspaces',
        header: 'Workspaces',
        cell: ({ row }) => <span>{row.original.workspaceIds.length}</span>,
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last Used',
        cell: ({ row }) =>
          row.original.lastUsedAt
            ? new Date(row.original.lastUsedAt).toLocaleDateString()
            : 'Never',
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        meta: { headerClassName: 'w-[80px]', cellClassName: 'text-right' },
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditItem(row.original)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete API client "${row.original.name}"? This will revoke the key.`
                    )
                  ) {
                    deleteApiClient(row.original.id, {
                      onSuccess: () =>
                        toast.success(`Deleted ${row.original.name}`),
                      onError: (e: Error) => toast.error(e.message),
                    });
                  }
                }}
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [deleteApiClient]
  );

  if (isLoading) return <div className="p-4 text-neutral-500">Loading...</div>;

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">API Clients</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4 mr-1" />
          Create API Client
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={apiClients}
        hidePagination
        options={{ getRowId: row => row.id }}
      />

      <CreateApiClientModal open={createOpen} onOpenChange={setCreateOpen} />

      {editItem && (
        <EditApiClientModal
          open={!!editItem}
          onOpenChange={open => !open && setEditItem(null)}
          apiClient={editItem}
        />
      )}
    </FlexCol>
  );
};

// ── Create Modal ──

const CreateApiClientModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [name, setName] = useState('');
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    []
  );
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const { createApiClient, isPending } = useAdminCreateApiClientService();

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const result = await createApiClient({
        name: name.trim(),
        workspaceIds: selectedWorkspaceIds,
      });
      setCreatedKey(result.apiKey);
      toast.success('API client created');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleClose = () => {
    setName('');
    setSelectedWorkspaceIds([]);
    setCreatedKey(null);
    onOpenChange(false);
  };

  if (createdKey) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Copy this key now. You won't be able to see it again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono bg-neutral-100 p-3 rounded break-all">
                {createdKey}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(createdKey);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API Client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Acme Corp"
            />
          </div>
          <WorkspaceTagSelector
            selectedWorkspaceIds={selectedWorkspaceIds}
            setSelectedWorkspaceIds={setSelectedWorkspaceIds}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            <Key className="size-4 mr-1" />
            Generate Key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Edit Modal ──

const EditApiClientModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiClient: ApiClientItem;
}> = ({ open, onOpenChange, apiClient }) => {
  const [name, setName] = useState(apiClient.name);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    apiClient.workspaceIds
  );
  const { updateApiClient, isPending } = useAdminUpdateApiClientService();

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    updateApiClient(
      {
        id: apiClient.id,
        body: {
          name: name.trim(),
          workspaceIds: selectedWorkspaceIds,
        },
      },
      {
        onSuccess: () => {
          toast.success('API client updated');
          onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit API Client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Client name"
            />
          </div>
          <div className="space-y-2">
            <Label>Key</Label>
            <code className="block text-xs font-mono bg-neutral-100 p-2 rounded text-neutral-500">
              {apiClient.keyPrefix}...
            </code>
          </div>
          <WorkspaceTagSelector
            selectedWorkspaceIds={selectedWorkspaceIds}
            setSelectedWorkspaceIds={setSelectedWorkspaceIds}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
